import C from '../../../constants';
import pick from 'lodash.pick';
import Alert from 'react-s-alert';
import moment from 'moment';

import {setUser} from '../../../store/actions/user';
import {SubmissionError} from 'redux-form';

import PolyUtil from '../../../util/PolyUtil';
import {hideConfirmModal, hideModal} from '../../../store/actions/display';
import RuntimeTools from "../../../util/RuntimeTools";
import {logoutUser} from "../../authentication/actions/authentication";
import {fetchListing} from "../../listings/actions/listings";

export const setOwner = (owner) => ({
    type: C.SET_OWNER,
    payload: owner
});

export const setOwnerProperties = (properties) => ({
    type: C.SET_OWNER_PROPERTIES,
    payload: properties
});

export const editPropertyListing = (listing, propId) => ({
    type: C.EDIT_PROPERTY_LISTING,
    payload: {listing, propId}
});

export const addProperty = (property) => ({
    type: C.ADD_PROPERTY,
    payload: property
});

export const setPropertyRoommateGroups = (roommate_groups, prop_id, listing_id) => ({
    type: C.SET_PROPERTY_ROOMMATE_GROUPS,
    payload: {
        prop_id: prop_id,
        listing_id: listing_id,
        roommate_groups: roommate_groups
    }
});

export const fetchOwnerProperties = () => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/properties/',
        {method: "GET"}
    ).then((data) => {
        return dispatch(setOwnerProperties(data));
    }).catch((error) => {
        console.log("Error: ", error);
    });
};

export const fetchPropertyRoommateGroups = (prop_id, listing_id) => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/properties/' + prop_id + '/listings/' + listing_id + '/roommate-groups/',
        {method: 'GET'}
    ).then((data) => {
        return dispatch(setPropertyRoommateGroups(data, prop_id, listing_id));
    }).catch((error) => {
        console.log("Error: ", error);
    });
};

export const fetchOwner = () => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/', {
        method: 'GET'
    }).then((data) => {
        //Update intercom details
        const details = data.intercom_details;
        RuntimeTools.updateIntercomDetails(details);
        RuntimeTools.updateFullstoryDetails(details);
        RuntimeTools.setRavenContext(data.email, 'landlord');

        dispatch(setUser(Object.assign({}, {user_type: 'landlord'}, pick(data, ['first_name', 'last_name', 'email', 'phone']))));

        return dispatch(setOwner(
            Object.assign(
                {},
                pick(data, [
                    'address_line_1',
                    'address_line_2',
                    'acceptance_message',
                    'city',
                    'email_verified',
                    'is_transunion_data_set',
                    'rejection_message',
                    'state',
                    'zip_code'
                ])
            )
        ));
    }).then(() => {
        return dispatch(fetchOwnerProperties());
    }).catch((error) => {
        console.log("Error: Invalid user token, logging out...");

        if (error.non_field_errors) {
            error._error = error.non_field_errors;
            delete error.non_field_errors;
        }
        if (error.detail === 'Invalid token.') { // logout if token invalid
            return dispatch(logoutUser());
        }
    });
};

export const refreshOwner = () => dispatch => {
    return dispatch(fetchOwnerProperties());
};

export const handleAddProperty = (property) => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/properties/',
        {
            method: 'POST',
            body: JSON.stringify(property)
        }
    ).then((data) => {
        return dispatch(addProperty(data));
    }).then(() => {
        return dispatch(hideModal('add_property_modal'));
    }).catch(error => {
        console.log('Error: ', error);
        if (error.non_field_errors) {
            error._error = error.non_field_errors;
            delete error.non_field_errors;
        }

        if (error._error[0].indexOf('The fields place_id, identifier must make a unique set.') !== -1) {
            throw new SubmissionError({_error: 'PolyRents already has a property with this address!'});
        } else {
            throw new SubmissionError(error);
        }
    });
};

export const handleAddListing = (listing, propId) => dispatch => {
    const form = Object.assign({}, listing, {
        lease_start: listing.lease_start.format ? listing.lease_start.format('YYYY-MM-DD') : moment(listing.lease_start, 'MM-DD-YYYY').format('YYYY-MM-DD'),
    });

    return PolyUtil.fetchNoResponse('/api/landlord/properties/' + propId + '/listings/', {
        method: 'POST',
        body: JSON.stringify(form)
    }).then(() => {
        return PolyUtil.fetchWrapper(
            '/api/landlord/properties/',
            {method: 'GET'}
        );
    }).then((data) => {
        return dispatch(setOwnerProperties(data));
    }).catch((error) => {
        console.log('Error: ', error);
        if (error.non_field_errors) {
            error._error = error.non_field_errors;
            delete error.non_field_errors;
        }

        return dispatch(hideModal('add_listing_modal'));
    });
};

export const handleEditListing = (listing, propId, listingId, applyKey) => dispatch => {
    const form = Object.assign({}, listing, {
        lease_start: listing.lease_start.format ? listing.lease_start.format('YYYY-MM-DD') : moment(listing.lease_start).format('YYYY-MM-DD'),
    });

    return PolyUtil.fetchNoResponse('/api/landlord/properties/' + propId + '/listings/' + listingId + '/', {
        method: 'PATCH',
        body: JSON.stringify(form)
    }).then(() => {
        return dispatch(editPropertyListing(form, propId));
    }).then(() => {
        return dispatch(fetchListing(applyKey))
    }).catch((error) => {
        console.log('Error: ', error);
        if (error.non_field_errors) {
            error._error = error.non_field_errors;
            delete error.non_field_errors;
        }

        return dispatch(hideModal('edit_listing_modal_' + propId));
    });
};

export const handleClaimProperty = (propId) => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/properties/' + propId + '/claim/',
        {method: "POST"}
    ).catch((error) => {
        console.log("Error: ", error);
    });
};

export const handleInviteTenants = (applicantCode, propId, listingId) => dispatch => {
    return PolyUtil.fetchNoResponse('/api/landlord/properties/' + propId + '/listings/' + listingId + '/invite-applicant/', {
        method: 'POST',
        body: JSON.stringify(applicantCode)
    }).then((data) => {
        return dispatch(hideModal('invite_tenant_modal'));
    }).catch((error) => {
        console.log('Error: ', error);
        if (error.non_field_errors) {
            error._error = error.non_field_errors;
            delete error.non_field_errors;
        }

        throw new SubmissionError(error);
    });
};

export const handleArchiveListing = (propId, listingId) => dispatch => {
    return PolyUtil.fetchWrapper('/api/landlord/properties/' + propId + '/listings/' + listingId + '/',
        {method: "DELETE"}
    ).then(() => {
        return dispatch(fetchOwnerProperties());
    }).then(() => {
        return dispatch(hideConfirmModal(propId));
    }).catch((error) => {
        console.log("Error: ", error);
    });
};

export const handleResendVerifyEmail = () => dispatch => {
    return PolyUtil.fetchNoResponse('/api/verify/email/',
        {method: "POST"}
    ).then(() => {
        Alert.success("Resent verification email!");
    }).catch((error) => {
        console.log("Error: ", error);
    });
};



// WEBPACK FOOTER //
// ./src/containers/owner/actions/owner.js